import { useState } from "react"
import { ComposedChart, Area, Line, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine, ResponsiveContainer } from "recharts"
import type { YearData } from "@/lib/projection-math"
import { useCurrency } from "@/context/currency/currency-context"
import { cn } from "@/lib/utils"

interface FireProjectionChartProps {
	data: YearData[]
	freedomYear: number | null
	annualGoal: number
}

const INCOME_COLOR = "#0ea5e9"
const PORTFOLIO_COLOR = "#8b5cf6"

export function FireProjectionChart({ data, freedomYear: _freedomYear, annualGoal }: FireProjectionChartProps) {
	const { format } = useCurrency()
	const [showPortfolioValue, setShowPortfolioValue] = useState(true)

	const convertedGoal = annualGoal // pre-converted to display currency in FirePage

	// Always show 30 years (matches scenario chart)
	const startYear = data[0]?.year ?? new Date().getFullYear()
	const endYear = startYear + 30
	const chartData = data
		.filter((d) => d.year <= endYear)
		.map((d) => ({ ...d, annualIncome: Math.round(d.annualIncome) }))

	// Left Y axis (income) — nice ticks
	const TICK_COUNT = 4
	const incomeMax = Math.max(...chartData.map((d) => d.annualIncome), convertedGoal, 1)
	const rawStep = incomeMax / TICK_COUNT
	const magnitude = Math.pow(10, Math.floor(Math.log10(rawStep)))
	const niceStep = Math.ceil(rawStep / magnitude) * magnitude
	const niceMax = niceStep * TICK_COUNT
	const ticks = Array.from({ length: TICK_COUNT + 1 }, (_, i) => i * niceStep)

	// Right Y axis (portfolio value) — nice ticks
	const pvValues = showPortfolioValue ? chartData.map((d) => d.portfolioValue) : [0]
	const pvMax = Math.max(...pvValues, 1)
	const pvRawStep = pvMax / TICK_COUNT
	const pvMagnitude = Math.pow(10, Math.floor(Math.log10(pvRawStep)))
	const pvNiceStep = Math.ceil(pvRawStep / pvMagnitude) * pvMagnitude
	const pvNiceMax = pvNiceStep * TICK_COUNT
	const pvTicks = Array.from({ length: TICK_COUNT + 1 }, (_, i) => i * pvNiceStep)

	return (
		<div className="space-y-3">
			<div className="flex flex-wrap gap-2">
				<button
					onClick={() => setShowPortfolioValue((v) => !v)}
					className={cn(
						"px-3 py-1 rounded-full text-xs font-medium border transition-all",
						showPortfolioValue
							? "text-white border-transparent"
							: "bg-transparent text-muted-foreground border-border",
					)}
					style={showPortfolioValue ? { backgroundColor: PORTFOLIO_COLOR } : {}}
				>
					Portfolio Value
				</button>
			</div>

			<div className="h-64">
				<ResponsiveContainer width="100%" height="100%">
					<ComposedChart tabIndex={-1} data={chartData} margin={{ top: 10, right: showPortfolioValue ? 60 : 48, left: 0, bottom: 0 }}>
						<defs>
							<linearGradient id="fireIncomeGradient" x1="0" y1="0" x2="0" y2="1">
								<stop offset="5%" stopColor={INCOME_COLOR} stopOpacity={0.3} />
								<stop offset="95%" stopColor={INCOME_COLOR} stopOpacity={0} />
							</linearGradient>
						</defs>
						<CartesianGrid strokeDasharray="3 3" className="stroke-border/50" />
						<XAxis dataKey="year" tick={{ fontSize: 11 }} />
						<YAxis
							yAxisId="income"
							ticks={ticks}
							domain={[0, niceMax]}
							tickFormatter={(v) => format(v, { compact: true, skipConversion: true })}
							tick={{ fontSize: 11 }}
							width={60}
						/>
						{showPortfolioValue && (
							<YAxis
								yAxisId="value"
								orientation="right"
								ticks={pvTicks}
								domain={[0, pvNiceMax]}
								tickFormatter={(v) => format(v, { compact: true, skipConversion: true })}
								tick={{ fontSize: 11, fill: PORTFOLIO_COLOR }}
								width={60}
							/>
						)}
						<Tooltip
							formatter={(value: number | undefined, name?: string) => [
								value != null ? format(value, { skipConversion: true }) : "—",
								name === "portfolioValue" ? "Portfolio Value" : "Annual Income",
							]}
							labelFormatter={(label) => `Year ${label}`}
						/>
						{annualGoal > 0 && (
							<ReferenceLine
								yAxisId="income"
								y={convertedGoal}
								stroke="#16a34a"
								strokeDasharray="6 3"
								label={{ value: "Goal", position: "right", fontSize: 11, fill: "#16a34a" }}
							/>
						)}
						<Area
							yAxisId="income"
							type="monotone"
							dataKey="annualIncome"
							stroke={INCOME_COLOR}
							strokeWidth={2}
							fill="url(#fireIncomeGradient)"
						/>
						{showPortfolioValue && (
							<Line
								yAxisId="value"
								type="monotone"
								dataKey="portfolioValue"
								stroke={PORTFOLIO_COLOR}
								strokeWidth={2}
								strokeDasharray="6 3"
								dot={false}
							/>
						)}
					</ComposedChart>
				</ResponsiveContainer>
			</div>
		</div>
	)
}
